import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Grid, CircularProgress } from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import DealCard from '../components/DealCard';
import FilterBar from '../components/FilterBar';
import MapView from '../components/MapView';
import Footer from '../components/Footer';
import { logger } from '../utils/logger';

const trimAddress = (address) => {
  if (!address) return '';
  return address.split(',')[0].trim();
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in km
};

const isTimeWithinSlot = (selectedHour, timeSlot) => {
  if (!timeSlot || !timeSlot.start || !timeSlot.end) return false;
  
  // Convert selected hour to number format (e.g., "14" -> 1400)
  const selectedTime = parseInt(selectedHour) * 100;
  
  return selectedTime >= timeSlot.start && selectedTime < timeSlot.end;
};

const HomePage = () => {
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('random');
  const [currentFilter, setCurrentFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [isOpenNow, setIsOpenNow] = useState(false);
  const [selectedTime, setSelectedTime] = useState({ day: 'all', hour: 'all' });
  const [locationStatus, setLocationStatus] = useState('pending'); // 'pending' | 'granted' | 'denied'

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
          setLocationStatus('granted');
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationStatus('denied');
        }
      );
    } else {
      setLocationStatus('denied');
    }
  }, []);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true);
        const happyHoursQuery = query(
          collection(db, 'venues'),
          where('category', '==', 'hh'),
          where('is_active', '==', true)
        );
        
        const querySnapshot = await getDocs(happyHoursQuery);
        logger.log('Query snapshot:', querySnapshot);
        
        if (querySnapshot.empty) {
          logger.log('No matching documents found');
          setDeals([]);
          setFilteredDeals([]);
          return;
        }
    
        const dealsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          logger.log('Document data:', doc.id, data);
          
          let distance = null;
          if (userLocation && data.lat && data.lon) {
            distance = calculateDistance(
              userLocation.lat,
              userLocation.lon,
              data.lat,
              data.lon
            );
          }

          // Transform data if needed to match component expectations
          return {
            id: doc.id,
            headline: data.headline, // Map headline to name for component use
            address: trimAddress(data.address), // Trim the address here
            main_offer: data.main_offer,
            offer_details: data.offer_details,
            days: data.days,
            phone: data.phone,
            link: data.link,
            image_url: data.image_url,
            place_id: data.place_id,
            lat: data.lat,
            lon: data.lon, // Note: components probably expect lng not lon
            category: data.category,
            opening_hours: data.opening_hours || {}, // Preserve the original structure
            randomValue: Math.random(),
            distance // Add the calculated distance
          };
        });
        
        logger.log('Processed deals data:', dealsData);
        setDeals(dealsData);
        setFilteredDeals(dealsData);
      } catch (error) {
        console.error('Error fetching deals:', error);
        setDeals([]);
        setFilteredDeals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [userLocation]);

  useEffect(() => {
    let result = [...deals];

    // Apply search filter if exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(deal => 
        deal.headline?.toLowerCase().includes(searchLower) ||
        deal.main_offer?.toLowerCase().includes(searchLower) ||
        deal.address?.toLowerCase().includes(searchLower)
      );
    }

    // Apply time and day filters
    if (selectedTime.day !== 'all' || selectedTime.hour !== 'all') {
      result = result.filter(deal => {
        // If filtering by day
        if (selectedTime.day !== 'all') {
          const daySlots = deal.opening_hours?.[selectedTime.day] || [];
          
          // If also filtering by hour
          if (selectedTime.hour !== 'all') {
            return daySlots.some(slot => isTimeWithinSlot(selectedTime.hour, slot));
          }
          
          // If only filtering by day
          return daySlots.length > 0;
        }
        
        // If only filtering by hour
        if (selectedTime.hour !== 'all') {
          return Object.values(deal.opening_hours || {}).some(daySlots =>
            daySlots.some(slot => isTimeWithinSlot(selectedTime.hour, slot))
          );
        }
        
        return true;
      });
    }

    // Apply open now filter
    if (isOpenNow) {
      const now = new Date();
      const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
      const currentTime = now.getHours() * 100 + now.getMinutes();

      result = result.filter(deal => {
        const todaySlots = deal.opening_hours?.[currentDay] || [];
        return todaySlots.some(slot => 
          slot && 
          typeof slot.start === 'number' && 
          typeof slot.end === 'number' && 
          currentTime >= slot.start && 
          currentTime < slot.end
        );
      });
    }

    // Apply sorting
    if (sortBy === 'distance' && userLocation) {
      result.sort((a, b) => {
        if (!a.distance || !b.distance) return 0;
        return parseFloat(a.distance) - parseFloat(b.distance);
      });
    } else if (sortBy === 'headline') {
      result.sort((a, b) => {
        const nameA = a.headline?.toLowerCase() || '';
        const nameB = b.headline?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    } else {
      result.sort((a, b) => a.randomValue - b.randomValue);
    }

    setFilteredDeals(result);
  }, [deals, searchTerm, sortBy, selectedTime, isOpenNow, userLocation]);

  const handleToggleMapView = () => {
    setShowMap(!showMap);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = ({ day, hour }) => {
    setSelectedTime({ day, hour });
  };

  const handleSortChange = (sortOption) => {
    if (sortOption === 'distance' && locationStatus !== 'granted') {
      return; // Don't allow distance sorting without location
    }
    setSortBy(sortOption);
  };

  const handleOpenNowChange = (isOpen) => {
    setIsOpenNow(isOpen);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header onToggleMap={handleToggleMapView} />
      
      <FilterBar 
        onSearch={handleSearch} 
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onOpenNowChange={handleOpenNowChange}
        currentFilter={selectedTime.day}
        currentSort={sortBy}
        isOpenNow={isOpenNow}
        userLocation={userLocation}
        locationStatus={locationStatus} // Add this prop
        sx={{ 
          position: 'sticky',
          top: { xs: 56, sm: 64 }, // Adjust for mobile header height
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      />

      <Container 
        maxWidth="lg" 
        sx={{ 
          py: { xs: 2, sm: 4 }, // Less padding on mobile
          px: { xs: 1, sm: 2, md: 3 }, // Less horizontal padding on mobile
          flexGrow: 1 
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : showMap ? (
          <MapView 
            deals={filteredDeals} 
            onClose={handleToggleMapView}
          />
        ) : filteredDeals.length > 0 ? (
          <Grid 
            container 
            spacing={{ xs: 2, sm: 3 }} // Less spacing on mobile
            sx={{
              justifyContent: 'center', // Center cards
              '& .MuiGrid-item': {
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
              }
            }}
          >
            {filteredDeals.map((venue) => (
              <Grid 
                item 
                key={venue.id} 
                xs={12}      // Full width on mobile
                sm={6}       // Two cards per row on tablet
                md={4}       // Three cards per row on desktop
                lg={3}       // Four cards per row on large screens
                sx={{
                  '& > *': { // Target DealCard directly
                    width: {
                      xs: '100%',    // Full width on mobile
                      sm: '280px'    // Original width on larger screens
                    },
                    maxWidth: {
                      xs: '400px',   // Limit width on mobile
                      sm: 'none'     // No limit on larger screens
                    },
                    minHeight: {
                      xs: '400px',   // Shorter on mobile
                      sm: '480px'    // Original height on larger screens
                    }
                  }
                }}
              >
                <DealCard venue={venue} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h5">No Happy Hour deals match your search.</Typography>
          </Box>
        )}
      </Container>
      
      <Footer />
    </Box>
  );
};

export default HomePage;